import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, Fade, Grid, Modal, Typography } from "@mui/material";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DashboardViewLayout from "../../components/_report/dashboard/DashboardViewLayout";
import DashboardSheetFilter from "../../components/common/DashboardSheetFilter";
import Controls from "../../components/controls/Controls";
import { getDashboarDetails, validateUserDashboard } from "../../config/services/dashboards";
import { getDashboarDetails as getPublishedDashboarDetails, validateUserDashboard as validateUserPublishedDashboard } from "../../config/services/publishDashboard";
import { useMeasureListContext } from "../../contexts/MeasureListContext";
import { savePDF } from "../../modifiedlibraries/pdfExport/react-to-pdf-converter";
import Loader from "../../pages/Loader";
import { ApplyBottomToastr } from "../../utils/Reusables";
import { DecryptData, EncryptData } from "../../utils/encryptDecrypt";
import { setLanguage } from "../../utils/multiLangDashboardV2Data";
import DashboardViewSheet from "./DashboardViewSheet";
import ErrorOutlineSharpIcon from '@mui/icons-material/ErrorOutlineSharp';
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  headingButton: {
    display: 'flex',
    marginRight: '16px',
    width: '100px',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start'
    },
  },
  headingButtonWrap: {
    width: '90%',
    float: 'right',
    padding: '0 1rem',
    textAlign: 'center',
    '&:hover': {
      backgroundColor: '#f45e29',
    },
    ['@media (max-width:1700px)']: {
      padding: '0 0.5rem',
      width: '95%',
      lineHeight: '16px',
      textAlign: 'center',
    },
    ['@media (max-width:1400px)']: {
      padding: '0.2rem 0.5rem',
      width: '100%',
      textAlign: 'center',
      lineHeight: '16px',
    },
    ['@media (max-width: 1199px)']: {
      float: 'left',
      maxWidth: '150px',
    }
  },
}));


const DashboardView = React.memo(() => {
  const { dashboardState, currentSheet } = useDashboardContext();
  const { dashboardName, dashboardDescription, dashboardUpdatedAt, isDeactivated, dashboardShareState, notificationBanner, pdfDownloadAllowed } = dashboardState;
  const { measureListByDataset, fetchMeasureListByDatasetId } = useMeasureListContext();
  const UrlParams = useParams();
  const classes = useStyles();
  const containerRef = useRef(null);
  const search = useLocation().search;
  const [restrictOption, setRestrictOption] = useState(false)
  const language = new URLSearchParams(search).get('lang');
  setLanguage(language)
  const navigate = useNavigate();
  const [downloadPdf , setDownloadPdf] = useState(false);

  const handleRedirectEditMode = () => {
    const data = {
      dashboardName: dashboardState?.dashboardName,
      sheets: dashboardState?.sheets,
      dashboardDescription: dashboardState?.dashboardDescription,
      dashboardUpdatedAt: dashboardState?.dashboardUpdatedAt,
      dashboardId: UrlParams?.id,
      tabDesign: dashboardState?.tabDesign,
      notificationBanner: dashboardState?.notificationBanner,
      pdfDownloadAllowed: dashboardState?.pdfDownloadAllowed,
      currentSheetIndex: dashboardState?.currentSheetIndex,
      dashboardShareState: dashboardState?.dashboardShareState,
      isDeactivated: dashboardState?.isDeactivated,
    }
    if (window.innerWidth < 900) {
      setRestrictOption(true);
    }
    else if (data?.dashboardId) {
      navigate(`/reportDashboard/${data?.dashboardId}`, { state: data })
    }
    else {
      navigate(`/reportDashboard/new`, { state: data })
    }
  }

  if (dashboardState?.loading) {
    return <Loader />
  }

  if (isDeactivated) {
    return (<Box width="100%" height="100%" pl={3} pr={3} className="embedded publish-dashboard">
      <Typography variant="h5" noWrap> Dashboard is deactivated. Contact Admin. </Typography>
    </Box>)
  }

  const RenderEditButton = () => {
    if (dashboardShareState && dashboardShareState?.[0]?.accessType === "view") return null
    return (
      <Box className="reports-embed-heading-right">
        <Controls.Button className="cm_ui_button reports-embed-heading-btn" text="Edit" onClick={handleRedirectEditMode} variant="contained" />
      </Box>
    )
  }

  const RenderPrintButton = () => {
    if (!pdfDownloadAllowed || downloadPdf) return null;
    return (
      <Box className={classes.headingButton} display="flex">
        <Controls.Button
          className={classes.headingButtonWrap + " " + "report_form_ui_btn"}
          style={{ padding: "20px 0px" }}
          variant="contained"
          onClick={()=>{setDownloadPdf(true)}}
          text={"Print PDF"}
        />
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      height="100%"
      className="embedded publish-dashboard publish-dashboard-view"
    >
      {dashboardState.loading && <Loader />}

      <Grid className="reports-embed-heading-section" >
        <Grid container mb={2} className="report-new-header-container" >
          <Grid container item alignItems="start" justifyContent="center" direction="column" className="reports-embed-header-content"
            sm={12} md={12} lg={6} order={{ lg: 1, md: 2, sm: 2, xs: 2 }} >
            <Typography variant="h2" noWrap>
              {dashboardName || ""}
            </Typography>
            <Typography variant="p" >
              {dashboardDescription}
            </Typography>
          </Grid>
        </Grid>
        <RenderPrintButton />
        <RenderEditButton />
      </Grid>

      <DashboardViewSheet />
      <DashboardSheetFilter />
      <div ref={containerRef}>
        {
          (currentSheet?.reports?.length)
            ? (
              <DashboardViewLayout
                selectedReports={currentSheet?.reports}
              />
            )
            : null
        }
      </div>
      <ExportDialog containerRef={containerRef} downloadPdf={downloadPdf} setDownloadPdf={setDownloadPdf}/>
      {
        notificationBanner
          ? <ApplyBottomToastr message="Reports get updated every 24 hours." />
          : null
      }
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className="modal-root"
        open={restrictOption}
        onClose={() => setRestrictOption(false)}
        closeAfterTransition
      >
        <Fade in={restrictOption}>
          <Box className="modal-paper modal-box modal-xs" id="transition-modal-title">
            <Box className="modal-content-simple text-center" sx={{ p: '1rem !important' }}>
              <ErrorOutlineSharpIcon color="primary" />
              <Typography variant="subtitle1" className="text-plain font-weight-normal">To use Custom Reports Feature, Switch to Desktop screen</Typography>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
});

export default React.memo(() => {
  return (
    <DashboardContextProvider>
      <DashboardView />
    </DashboardContextProvider>
  )
})

const DashBoardContext = createContext();
function dashboardReducer(state, action) {
  switch (action.type) {
    case "FETCH_DASHBOARD_DETAILS_SUCCESS":
      return {
        ...state,
        loading: false,
        dashboardName: action.payload.dashboardName,
        dashboardDescription: action.payload.dashboardDescription,
        sheets: action.payload.sheets,
        dashboardUpdatedAt: action.payload.dashboardUpdatedAt,
        tabDesign: action.payload.tabDesign,
        notificationBanner: action.payload.notificationBanner,
        pdfDownloadAllowed: action.payload.pdfDownloadAllowed,
        isDeactivated: action.payload.isDeactivated,
        dashboardShareState: action.payload.dashboardShareState,
        currentSheetIndex: action.payload.currentSheetIndex,
      };
    case "FETCH_DASHBOARD_DETAILS_ERROR":
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        errorMeassage: action.payload.errorMeassage
      };

    case "SET_CURRENT_SHEET_INDEX":
      const { currentSheetIndex, sheet } = action.payload;
      let sheets = [...state.sheets];
      if (sheet) {
        sheets[currentSheetIndex] = sheet;
      }
      return {
        ...state,
        currentSheetIndex,
        sheets
      }
    case "UPDATE_SHEET":
      const _sheets = [...state.sheets];
      _sheets[state.currentSheetIndex] = action.payload.sheet;
      return {
        ...state,
        sheets: _sheets
      }

    case "ADD_NEW_SHEET":
      return {
        ...state,
        sheets: [
          ...state.sheets,
          {
            sheetName: `Sheet ${state.sheets.length + 1}`,
            sheetId: `sheet${state.sheets.length + 1}`,
            reports: [],
            sheetFilter: [],
          }
        ]
      }

    case "UPDATE_SHEET_BY_INDEX":
      const toUpdatesheets = [...state.sheets];
      toUpdatesheets[action.payload.sheetIndex] = action.payload.sheet;
      return {
        ...state,
        sheets: toUpdatesheets
      }
    case "DELETE_SHEET_BY_INDEX":
      const toDeleteFromSheets = [...state.sheets];
      toDeleteFromSheets.splice(action.payload.sheetIndex, 1);
      if (toDeleteFromSheets.length === 0) {
        toDeleteFromSheets.push({
          sheetName: 'Sheet 1',
          sheetId: 'sheet1',
          reports: [],
          sheetFilter: [],
        })
      }
      const newSheetIndex = action.payload.sheetIndex === state.currentSheetIndex
        ? (action.payload.sheetIndex - 1 < 0 ? 0 : action.payload.sheetIndex - 1)
        : state.currentSheetIndex;
      localStorage.setItem('currentSheetIndex', EncryptData(newSheetIndex))
      return {
        ...state,
        sheets: toDeleteFromSheets,
        currentSheetIndex:
          action.payload.sheetIndex === state.currentSheetIndex
            ? (action.payload.sheetIndex - 1 < 0 ? 0 : action.payload.sheetIndex - 1)
            : state.currentSheetIndex,
      }
    case "DUPLICATE_SHEET_BY_INDEX":
      state.sheets.splice(action.payload.sheetIndex + 1, 0, action.payload.sheet);
      return {
        ...state,
      }

    case "NEW_DASHBOARD":
      return {
        ...state,
        loading: false,
        dashboardName: 'New Dashboard',
        dashboardDescription: '',
        sheets: [
          {
            sheetName: 'Sheet 1',
            sheetId: 'sheet1',
            reports: [],
            sheetFilter: [],
          }
        ],
        dashboardUpdatedAt: '',
        tabDesign: 'old',
        notificationBanner: false,
        pdfDownloadAllowed: false,
        isDeactivated: false,
        dashboardShareState: [],
        currentSheetIndex: 0,
      }
    case "UPDATE_DASHBOARD":
      return {
        ...state,
        dashboardName: action.payload.dashboardName || state.dashboardName,
        dashboardDescription: action.payload.dashboardDescription || state.dashboardDescription,
        dashboardUpdatedAt: action.payload.dashboardUpdatedAt || state.dashboardUpdatedAt,
        tabDesign: action.payload.tabDesign || state.tabDesign,
        notificationBanner: action.payload.notificationBanner ?? state.notificationBanner,
        pdfDownloadAllowed: action.payload.pdfDownloadAllowed ?? state.pdfDownloadAllowed,
        isDeactivated: action.payload.isDeactivated || state.isDeactivated,
        dashboardShareState: action.payload.dashboardShareState || state.dashboardShareState,
      }
    case "UPDATE_ALL_SHEETS":
      return {
        ...state,
        sheets: action.payload.sheets || state.sheets,

      }
    default:
      return state;
  }
}
async function fetchDashboardDetails({ dashboardId, dispatch, navigate, applySheetFilters, isPublishedDashboardPage, dashboardState, currentSheetIndex = 0 }) {
  try {
    let validateUserDashboardResponse;

    if (isPublishedDashboardPage) {
      validateUserDashboardResponse = await validateUserPublishedDashboard(dashboardId);
    } else {
      validateUserDashboardResponse = await validateUserDashboard(dashboardId);
    }

    if (!validateUserDashboardResponse && validateUserDashboardResponse?.length) {
      navigate("/unauthorized")
    }

    let dashboardResponse
    if (isPublishedDashboardPage) {
      dashboardResponse = await getPublishedDashboarDetails({ dashboardId });
    } else {
      dashboardResponse = await getDashboarDetails({ dashboardId });
    }

    if (!dashboardResponse?.result) {
      dispatch({
        type: "FETCH_DASHBOARD_DETAILS_ERROR",
        payload: {
          error: true,
          errorMeassage: 'Something went wrong. Please try again later.'
        }
      })
    }

    const { sheets, sharedWith } = dashboardResponse?.result;
    const userTypeId = await DecryptData(localStorage.getItem('userTypeId'))
    const dashboardShareState = await sharedWith?.filter(item => item?.usertypeId === userTypeId);
    let formatedSheets = await Promise.all(sheets?.map(async sheet => {
      let sheetReports = await sheet?.reports?.map(report => {
        return {
          ...report,
          ...report?.reportId,
          label: report?.reportId?.reportName,
          value: report?.reportId?._id,
          reportId: report?.reportId?._id,
          reportName: report?.reportId?.reportName,
          linkedReports: report?.reportId?.linkedReports?.map(linkedReport => ({ ...linkedReport, reportName: linkedReport?.reportId?.reportName }))
        }
      });
      if (sheet?.dashboardSections && sheet?.dashboardSections?.length) {
        await sheet.dashboardSections.forEach(section => {
          if (section?.reportIndex !== undefined || section?.reportIndex !== null)
            sheetReports = [
              ...sheetReports.slice(0, section.reportIndex),
              section,
              ...sheetReports.slice(section.reportIndex)
            ]
        });
      }
      sheet.reports = sheetReports;
      return sheet;
    }))
    formatedSheets = await formatedSheets.filter(sheet => sheet !== null && sheet !== undefined);

    const currentSheetWithAppliedFilters = await applySheetFilters({
      sheet: formatedSheets[currentSheetIndex],
      sheetFilters: formatedSheets[currentSheetIndex]?.sheetFilter,
    });
    formatedSheets[currentSheetIndex] = currentSheetWithAppliedFilters;
    await dispatch({
      type: "FETCH_DASHBOARD_DETAILS_SUCCESS",
      payload: {
        dashboardName: dashboardResponse?.result?.dashboardName,
        dashboardDescription: dashboardResponse?.result?.dashboardDescription,
        dashboardUpdatedAt: dashboardResponse?.result?.updatedAt,
        tabDesign: dashboardResponse?.result?.settings?.tabDesign,
        notificationBanner: dashboardResponse?.result?.settings?.notificationBanner,
        pdfDownloadAllowed: dashboardResponse?.result?.settings?.pdfDownloadAllowed,
        isDeactivated: dashboardResponse?.result?.isDeactivated,
        sheets: formatedSheets,
        currentSheetIndex,
        loading: false,
        dashboardShareState,
        error: null,
        errorMeassage: null,
      }
    })
  } catch (error) {
    //console.log(error)
    dispatch({
      type: "FETCH_DASHBOARD_DETAILS_ERROR",
      payload: error,
      errorMeassage: error.message
    })
  }
}

export const DashboardContextProvider = ({ children, isEditDashboardPage = false, isPublishedDashboardPage = false, getUrlFilters }) => {
  const { measureListByDataset, fetchMeasureListByDatasetId } = useMeasureListContext();
  const { navigate } = useNavigate();
  const UrlParams = useParams();
  const [searchParams] = useSearchParams();
  const { state: locationState } = useLocation();
  async function applyFilterToReport(report, sheetFilters, oldSheetFilters = []) {
    try {
      await Promise.all(await sheetFilters.map(async (sheetFilter) => {
        const sheetFilterMemberMeasure = sheetFilter?.member.split('.')[1];

        const sheetFilterInReportIndex = await report?.reportFilters?.findIndex(
          (reportFilter) =>
            reportFilter?.member.split('.')[1] === sheetFilterMemberMeasure &&
            reportFilter?.operator === sheetFilter?.operator
        );
        if (sheetFilterInReportIndex !== -1) {
          if (oldSheetFilters.length > 0) {
            // clear filter values
            const oldSheetFilter = await oldSheetFilters.find(oldSheetFilter => oldSheetFilter?.member === sheetFilter?.member);
            if (oldSheetFilter) {
              let values = [];
              // if (sheetFilter?.listType === 'multipleselect' && sheetFilter?.values?.length > 0) {
              //   values = [...oldSheetFilter?.values];
              // }
              report.reportFilters[sheetFilterInReportIndex].values = sheetFilter?.type === 'time' ? sheetFilter?.values : sheetFilter?.values;
              return report;
            }
          }
          report.reportFilters[sheetFilterInReportIndex].values = sheetFilter.values;
          return report;
        }
        const dataSetId = report?.dataSetId?._id || report?.dataSetId;
        const measureList = await (measureListByDataset[dataSetId] || await fetchMeasureListByDatasetId(dataSetId));
        const measureExist = await measureList?.find((measure) => measure?.name === sheetFilterMemberMeasure);
        if (!measureExist) {
          return report;
        }

        const dataSetName = report?.dataSetId?.dataSetName || report?.measures?.[0]?.fieldName?.split('.')[0] || report?.dimensions?.[0]?.fieldName?.split('.')[0];
        await report.reportFilters.push({
          member: `${dataSetName}.${sheetFilterMemberMeasure}`,
          operator: sheetFilter?.operator,
          values: sheetFilter?.values,
          sheetFilter: true,
        });

        return report;
      }));
      if (isPublishedDashboardPage) {
        const urlFilters = await getUrlFilters(report);
        report.reportFilters = [...report.reportFilters, ...urlFilters];
      }
      return report;
    } catch (error) {
      // console.log('applyFilterToReport error : ', error)
      return report;
    }
  }

  async function applyFilterWithLinkedReports(report, sheetFilters, oldSheetFilters = []) {
    try {
      let updatedReport = report
      updatedReport = await applyFilterToReport(report, sheetFilters, oldSheetFilters);
      let updatedLinkedReports = await Promise.all(report.linkedReports.map(async (linkedReport) => {
        let { reportId } = linkedReport;
        reportId = await applyFilterToReport(reportId, sheetFilters, oldSheetFilters);
        return {
          ...linkedReport,
          reportId
        }
      }));
      updatedReport.linkedReports = updatedLinkedReports;
      return updatedReport;
    } catch (error) {
      // console.log('apply filter error : ', error)
    }
  }

  async function applySheetFilters({ sheetFilters = [], sheet }) {
    //console.log('ApplySheetFilters',sheetFilters)
    try {
      //if (!sheetFilters?.length) return sheet;
      const currentSheet = sheet || dashboardState?.sheets?.[dashboardState?.currentSheetIndex];
      const updatedReports = await (await Promise.allSettled(currentSheet?.reports?.map(async (report) => {
        if (report.linkedReports?.length) {
          return await applyFilterWithLinkedReports(report, sheetFilters, sheet.sheetFilter);
        }
        return await applyFilterToReport(report, sheetFilters, sheet.sheetFilter);
      }))).filter(({ status, ...rest }) => status === 'fulfilled').map(({ value }) => value);
      currentSheet.reports = updatedReports;
      return currentSheet;
    } catch (error) {
      // console.log('error in applySheetFilters : ', error);
      return sheet;
    }
  }

  const [dashboardState, dispatch] = useReducer(
    dashboardReducer,
    {
      loading: true,
      error: null,
      errorMeassage: null,
    },
    () => {
      if (locationState && Object.keys(locationState).length) {
        if (locationState?.dashboardId) UrlParams.id = locationState?.dashboardId
        return {
          loading: false,
          error: null,
          errorMeassage: null,
          dashboardName: locationState?.dashboardName,
          dashboardDescription: locationState?.dashboardDescription,
          sheets: locationState?.sheets,
          dashboardUpdatedAt: locationState?.dashboardUpdatedAt,
          tabDesign: locationState?.tabDesign,
          notificationBanner: locationState?.notificationBanner,
          pdfDownloadAllowed: locationState?.pdfDownloadAllowed,
          currentSheetIndex: locationState?.currentSheetIndex,
          dashboardShareState: locationState?.dashboardShareState,
          isDeactivated: locationState?.isDeactivated,
        }
      }
      return {
        loading: true,
      }
    }
  );
  const applyFilterToAllSheets = useCallback(
    (sheetFilter) => {
      const sheets = JSON.parse(JSON.stringify(dashboardState?.sheets || []));

      if (!sheetFilter) return;

      if (sheetFilter?.applyTo === "allSheets") {
        const updatedSheets = sheets.map((sheet) => {
          if (!sheet?.sheetFilter) return sheet;

          const updatedSheetFilter = sheet.sheetFilter.map((filter) => {
            if (filter?.member?.split('.')[1] === sheetFilter?.member?.split('.')[1]) {
              return {
                ...filter,
                values: JSON.parse(JSON.stringify(sheetFilter.values)),
              };
            }
            return filter;
          });

          return { ...sheet, sheetFilter: updatedSheetFilter };
        });

        dispatch({
          type: "UPDATE_ALL_SHEETS",
          payload: { sheets: updatedSheets },
        });
      }
    },
    [dashboardState, dispatch]
  );

  useEffect(() => {
    if (!UrlParams.id || locationState?.dashboardId) return;
    if (UrlParams.id === 'new' && isEditDashboardPage) {
      dispatch({
        type: "NEW_DASHBOARD",
      })
      return;
    }

    let currentSheetIndex = 0;
    // publidhed dashboard
    if (isPublishedDashboardPage && !searchParams.get('filterParams')) {
      if (localStorage.getItem('publishedDashboardCurrentSheetIndex')) {
        currentSheetIndex = DecryptData(localStorage.getItem('publishedDashboardCurrentSheetIndex'))
      }
    }
    // non published dashboard
    if (!isPublishedDashboardPage) {
      if (localStorage.getItem('currentSheetIndex')) {
        currentSheetIndex = DecryptData(localStorage.getItem('currentSheetIndex'))
      }
    }

    fetchDashboardDetails({
      dashboardId: UrlParams.id, dispatch, navigate, applySheetFilters, isPublishedDashboardPage, dashboardState, currentSheetIndex
    })
  }, [UrlParams.id]);

  useEffect(() => {
    if (dashboardState?.sheets?.length && dashboardState?.currentSheetIndex !== undefined) {
      const additionalFilters = JSON.parse(localStorage.getItem('additionalFilters'));
      if (additionalFilters) {
        const currentSheet = dashboardState?.sheets[dashboardState.currentSheetIndex];
        const updatedSheetFilters = currentSheet?.sheetFilter?.map(filter => {
          const member = filter?.member?.split('.')[1];
          if (additionalFilters?.[member]) {
            return {
              ...filter,
              values: additionalFilters?.[member]
            };
          }
          return filter;
        });
        applyFiltersOnCurrentSheet({
          sheetFilters: updatedSheetFilters,
          filterIndex: null,
          sheetFilter: null
        });
        localStorage.removeItem('additionalFilters');
      }
    }
  }, [dashboardState?.sheets, dashboardState?.currentSheetIndex]);

  const currentSheet = useMemo(() => dashboardState?.sheets?.[dashboardState?.currentSheetIndex], [dashboardState?.currentSheetIndex, dashboardState?.sheets]);

  async function changeSheet(currentSheetIndex) {
    try {
      if (currentSheetIndex === null || currentSheetIndex === undefined || !dashboardState?.sheets?.[currentSheetIndex]) return;
      let sheet = await structuredClone(dashboardState?.sheets?.[currentSheetIndex]);
      sheet = await applySheetFilters({ sheet: dashboardState?.sheets?.[currentSheetIndex], sheetFilters: sheet?.sheetFilter });
      if (isPublishedDashboardPage) {
        localStorage.setItem('publishedDashboardCurrentSheetIndex', EncryptData(currentSheetIndex))
      } else {
        localStorage.setItem('currentSheetIndex', EncryptData(currentSheetIndex))
      }
      dispatch({
        type: "SET_CURRENT_SHEET_INDEX",
        payload: { currentSheetIndex, sheet }
      })
    } catch (error) {
      // console.log('change sheet error : ', error)
    }
  }

  async function applyFiltersOnCurrentSheet({ sheetFilters, filterIndex, sheetFilter }) {
    try {

      if (!sheetFilters?.length && !sheetFilter && !filterIndex) return;
      let sheet = await structuredClone(currentSheet);
      if (sheetFilters?.length) {
        sheet = await applySheetFilters({ sheet, sheetFilters });
        sheet.sheetFilter = sheetFilters;
        if(sheetFilters[filterIndex]?.applyTo === "allSheets"){
          applyFilterToAllSheets(sheetFilters[filterIndex]);
        }
        dispatch({
          type: "UPDATE_SHEET",
          payload: { sheet }
        })
        return
      }

      if (sheetFilter.values.length === 0 && sheetFilter?.useRelevant?.length !== 0) {
        const cascadedFilters = await removeCascadedFilterValues({ sheetFilter, sheetFilters: sheet.sheetFilter })
        sheet = await applySheetFilters({ sheet, sheetFilters: cascadedFilters });
        sheet.sheetFilter = cascadedFilters;
      } else {
        sheet = await applySheetFilters({ sheet, sheetFilters: [sheetFilter] });
        sheet.sheetFilter[filterIndex] = sheetFilter;
      }
      if(sheetFilter?.applyTo === "allSheets"){
        applyFilterToAllSheets(sheetFilter);
      }
      dispatch({
         type: "UPDATE_SHEET",
         payload: { sheet }
     })
    } catch (error) {
      // console.log('applyFiltersOnCurrentSheet error : ', error)
    }
  }

  function removeCascadedFilterValues({ sheetFilter, sheetFilters }) {
    try {
      let _sheetFilters = [...sheetFilters];
      if (sheetFilter?.useRelevant?.length !== 0) {
        const filterIndex = sheetFilters.findIndex(({ member }) => member === sheetFilter?.member);
        if (filterIndex === -1) return sheetFilters;

        _sheetFilters[filterIndex] = sheetFilter;

        sheetFilter?.useRelevant?.forEach(cascadeFilterRelevant => {
          const cascadeFilterIndex = _sheetFilters.findIndex(({ member }) => member === cascadeFilterRelevant.relevantMember);
          if (cascadeFilterIndex === -1) return;

          _sheetFilters[cascadeFilterIndex].values = [];
          if (_sheetFilters[cascadeFilterIndex]?.useRelevant?.length !== 0) {
            const removed = removeCascadedFilterValues({ sheetFilter: _sheetFilters[cascadeFilterIndex], sheetFilters: _sheetFilters })
            _sheetFilters = removed;
          }
        });
      }
      return _sheetFilters;
    } catch (error) {
      // console.log('removeCascadedFilterValues error : ', error)
    }
  }

  async function updateSheet({ sheetIndex, sheet }) {
    dispatch({
      type: "UPDATE_SHEET_BY_INDEX",
      payload: { sheetIndex, sheet }
    })
  }

  async function updateSheetFilter({ sheetIndex, sheetFilter, metaDataOnly = false }) {
    let sheet = await structuredClone(currentSheet);
    sheet.sheetFilter = sheetFilter;
    if (!metaDataOnly) {
      sheet = await applySheetFilters({ sheet, sheetFilters: sheetFilter });
    }
    dispatch({
      type: "UPDATE_SHEET_BY_INDEX",
      payload: { sheetIndex, sheet }
    })
  }

  async function addNewSheet() {
    dispatch({ type: 'ADD_NEW_SHEET' })
  }

  async function deleteSheet({ sheetIndex }) {
    let sheet = dashboardState.sheets[sheetIndex];
    if (sheet) {
      dispatch({
        type: "DELETE_SHEET_BY_INDEX",
        payload: { sheetIndex, sheet }
      })
    }
  }

  async function duplicateSheet({ sheetIndex, sheet }) {
    dispatch({
      type: "DUPLICATE_SHEET_BY_INDEX",
      payload: { sheetIndex, sheet }
    })
  }

  async function updateDashboard(dashboard = {}) {
    dispatch({
      type: "UPDATE_DASHBOARD",
      payload: { ...dashboard }
    })
  }

  async function addReportToCurrentSheet({ report }) {
    let sheet = await structuredClone(currentSheet);
    let lastReport = currentSheet.reports.length > 0 ? currentSheet.reports.reduce(function(prev, current) {
      return (prev && prev.y > current.y) ? prev : current
    }) : null
    if(lastReport){
      report['y'] = lastReport.y + 15
      report['x'] = 0
    }
    if (report?.reportType === 'section') {
      let section = structuredClone(report);
      section.reportIndex = section.reportIndex ?? sheet.reports.length
      sheet.dashboardSections = [...sheet?.dashboardSections ?? [], report]
      sheet.reports = [
        ...sheet.reports.slice(0, section.reportIndex),
        section,
        ...sheet.reports.slice(section.reportIndex)
      ];
    } else {
      sheet.reports.push(report);
      sheet = await applySheetFilters({ sheet, sheetFilters: sheet?.sheetFilter });
    }
    dispatch({
      type: "UPDATE_SHEET",
      payload: { sheet }
    })
  }

  async function updateReportOptions({report,reportIndex}){
    let sheet = await structuredClone(currentSheet);
    sheet.reports[reportIndex] =  report
    //console.log(sheet.reports)
    dispatch({
      type: "UPDATE_SHEET",
      payload: { sheet }
    })
  }

  async function removeReportFromCurrentSheet(report) {
    let sheet = await structuredClone(currentSheet);
    // id for sections
    sheet.reports = sheet.reports.filter(({ _id, id, reportType }) => {
      if (reportType && reportType === 'section') return id !== report?.id
      return _id !== report?._id
    });
    dispatch({
      type: "UPDATE_SHEET",
      payload: { sheet }
    })
  }

  async function updateReportInCurrentSheet({ report }) {
    let sheet = await structuredClone(currentSheet);
    sheet.reports = sheet.reports.map((_report) => {
      if (_report?._id === report?._id || _report?.id === report?.id) {
        return report;
      }
      return _report;
    });
    dispatch({
      type: "UPDATE_SHEET",
      payload: { sheet }
    })
  }
  return (
    <DashBoardContext.Provider value={{ dashboardState, dispatch,updateReportOptions, applyFiltersOnCurrentSheet, currentSheet, changeSheet, updateSheet, updateDashboard, addReportToCurrentSheet, removeReportFromCurrentSheet, deleteSheet, duplicateSheet, addNewSheet, updateReportInCurrentSheet, updateSheetFilter }}>
      {children}
    </DashBoardContext.Provider>
  )
}

export function useDashboardContext() {
  const context = useContext(DashBoardContext);
  if (context === undefined) {
    throw new Error(
      `useDashboardContext must be used within a DashboardContextProvider`
    );
  }
  return context;
}

function ExportDialog({
  containerRef,
  downloadPdf = false,
  setDownloadPdf
}) {
  const search = useLocation().search;
  const IsExportUrl = new URLSearchParams(search).get('export');
  const [exportProcessDialog, setExportProcessDialog] = useState(false);
  const exportPDFWithMethod = () => {
    let element = containerRef.current || document.body;
    savePDF(
      element,
      {
        paperSize: "auto",
        margin: 40,
        fileName: `Report for ${new Date().getFullYear()}`
      },
      () => {
        !downloadPdf && window.close();
        setDownloadPdf(false);
        setExportProcessDialog(false);
      }
    );
  };

  useEffect(() => {
    if (IsExportUrl === 'true' || IsExportUrl === true || downloadPdf) {
      setExportProcessDialog(true)
      setTimeout(() => exportPDFWithMethod(), [40000])
    }
  }, [downloadPdf]);
  return (
    <Dialog aria-labelledby="customized-dialog-title" open={exportProcessDialog}>
      <DialogTitle id="customized-dialog-title" >Export in Progress... </DialogTitle>
      <DialogContent dividers>
        <Typography > We're working to export the dashboard... <CircularProgress /> </Typography>
      </DialogContent>
    </Dialog>
  )
}